<template>
    <div
    ref="contactRef"
    class="contact-item"
    :class="{'no-padding': withActions, 'disabled': phoneContactData.selected}">
        <!-- Contact image -->
        <div class="contact-img">
            <img v-if="phoneContactData.imageData" :src="`data:image/png;base64, ${phoneContactData.imageData}`" alt="Contact Image">
            <div v-else class="acronym">{{acronymBuilder(getContactInfo.name)}}</div>
        </div>
        <!-- Contact Text -->
        <div class="contact-text" :style="{'max-width': `${textMaxWidth}px`}" @click="contactClickedHandler">
            <div class="contact-name">{{ getContactInfo.name }}</div>
            <div class="contact-info">{{ getContactInfo.text }}</div>
        </div>
        <!-- BAI Icon -->
        <logo-icon
        v-if="getContactInfo.isEnrolled && !getContactInfo.applyMultipleContactButton"/>
        <!-- *********************************************
        *                    Buttons                     *
        ********************************************** -->
        <div v-if="withActions" class="buttons-wrapper">
            <!-- Clear Button -->
            <button-quaternary icon="close" @click="emit('clear')"/>
            <!-- Contact Button -->
            <button-quaternary icon="contact" @click="emit('contactButtonClicked')"/>
        </div>
        <div v-else class="buttons-wrapper">
            <!-- Selected Icon -->
            <icon-check-circle class="check-icon" v-if="getContactInfo.selected"/>
            <!-- Multiple Contacts Button -->
            <button-quaternary
            v-if="getContactInfo.applyMultipleContactButton"
            icon="arrow-right-full"
            @click="showMultipleContactsModal = true"/>
        </div>
    </div>
    <!-- *********************************************
	*             Multiple Contacts Modal            *
	********************************************** -->
	<modal-centered-generic
    v-if="showMultipleContactsModal"
    rightButtonIcon="check-line"
    :triggerClose="triggerClose"
    :titleIcon="sendType === 'email' ? 'letter' : 'chat'"
    :title="sendType === 'email' ? t('modals.multipleContacts.titleEmail') : t('modals.multipleContacts.titleNumber')"
    :text="sendType === 'email' ? t('modals.multipleContacts.textEmail') : t('modals.multipleContacts.textNumber')"
    :leftButtonText="t('modals.multipleContacts.cancelButton')"
    :rightButtonText="t('modals.multipleContacts.selectedButton')"
    @hideMe="showMultipleContactsModal = false"
    @rightButtonClicked="confirmMultipleContactClick">
		<selector-radio-group
        style="margin-top: 16px;"
		:radioParams="getContactInfo.contacts"
		@clicked="multipleContactSelected = $event"/>
	</modal-centered-generic>
</template>

<script setup>
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import IconCheckCircle from '@/assets/icons/check-circle.svg?inline'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import SelectorRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import LogoIcon from '@/assets/images/logo.svg?inline'
/* Vue & Assets */
import { ref, computed, onMounted } from 'vue'
import { acronymBuilder } from '@/assets/scripts/strings'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['contactSelected', 'clear', 'contactButtonClicked', 'multipleContactsAction'])

/* Props
@phoneContactData {Object} - object with:
    - firstName {String}
    - middleName {String}
    - familyName {String}
    - imageData {String} - base64 img string
    - contactData {String} - email or phone number
@withActions {Boolean} - prop to show delete and contacts buttons
@sendType {String} - send information type ['email', 'sms']
*/
const props = defineProps({
    phoneContactData: Object,
    withActions: Boolean,
    sendType: String
})

/* Elements Ref Access */
const contactRef = ref('')
const textMaxWidth = ref(0)
const showMultipleContactsModal = ref(false)
const triggerClose = ref(false)
const multipleContactSelected = ref('')

const getContactInfo = computed(() => {
    const sendTypeToGet = props.sendType === 'email' ? 'emails' : 'phoneNumbers'
    const data = props.phoneContactData
    let name = ''
    if (data.firstName) name += data.firstName + ' '
    if (data.middleName) name += data.middleName + ' '
    if (data.familyName) name += data.familyName

    if (!name.length) name = data.displayName

    const firstEnrolledBaiContact = data[sendTypeToGet].find(item => item.isEnrolled)

    const contactInfo = {
        id: data.id,
        name: firstEnrolledBaiContact ? firstEnrolledBaiContact.enrolledInfo.name : name,
        firstName: data.firstName,
        middleName: data.middleName,
        familyName: data.familyName,
        text: '',
        selected: data.selected,
        isEnrolled: data.phoneNumbers.length === 1 && data.phoneNumbers[0].isEnrolled,
        action: null,
        applyMultipleContactButton: data[sendTypeToGet].length > 1,
        contacts: data[sendTypeToGet].map((item, index) => {
            return {
                ...item,
                text: item.value,
                preventLabelAutoCapitalize: true,
                selected: multipleContactSelected.value ? multipleContactSelected.value === item.id : (!firstEnrolledBaiContact ? index === 0 : firstEnrolledBaiContact.id === item.id),
                hasIcon: item.isEnrolled,
                iconPath: 'images/logo'
            }
        })
    }

    if (!props.withActions) {
        if (data[sendTypeToGet].length === 1) {
            contactInfo.text = data[sendTypeToGet][0].value
        } else {
            contactInfo.text = props.sendType === 'email' ? t('modals.contacts.multipleEmails') : t('modals.contacts.multipleNumbers')
        }
        return contactInfo
    }

    contactInfo.text = data.contactData
    return contactInfo
})

const contactClickedHandler = () => {
    if (getContactInfo.value.applyMultipleContactButton) showMultipleContactsModal.value = true
    else emit('contactSelected', { contact: getContactInfo.value, contactData: getContactInfo.value.contacts[0].value })
}

const confirmMultipleContactClick = () => {
    emit('contactSelected', { contact: getContactInfo.value, contactData: getContactInfo.value.contacts.find(obj => obj.selected).value })
    triggerClose.value = true
}

onMounted(() => {
    textMaxWidth.value = contactRef.value.clientWidth - 128 // This is to prevent elements being hidden when in small mobile resolutions
})
</script>

<style lang="scss" scoped>
.contact-item {
    width: 100%;
    height: 64px;
    padding-left: 16px;

    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;

    border-bottom: 1px solid var(--secondary-light-2);

    &:last-of-type {
        margin-bottom: 24px;
    }

    .contact-img {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 32px;
        height: 32px;
        overflow: hidden;
        flex-shrink: 0;

        border-radius: 50%;

        img, .acronym {
            width: 32px;
            height: 32px;

            border-radius: 50%;
        }

        .acronym {
            @include H7-Medium;
            text-align: center;
            line-height: 28px;

            background-color: var(--secondary-light-4);
            border: 2px solid var(--secondary-light-2);

            color: var(--secondary-dark-2);
        }
    }

    .contact-text {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex-grow: 1;

        .contact-name {
            @include H7-Medium;
            color: var(--secondary-dark-2);

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .contact-info {
            @include H7-Regular;
            color: var(--secondary-dark-2);

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .buttons-wrapper {
        display: flex;
        gap: 12px;
    }

    &.disabled {
        pointer-events: none;

        .contact-img,
        .contact-text {
            opacity: 0.6;
        }

        .check-icon {
            position: absolute;
            top: 26px;
            right: 16px;

            width: 20px;
            height: 20px;
        }
    }
}
</style>
